import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Switch, Route, useLocation, Redirect } from 'react-router-dom';
import classnames from 'classnames';

import {
  ADD_FEATURE_TYPE,
  FEEDBACK_WIZARD_STEP,
  namedRoute,
  pathnameMatches,
  getPathnameMatch,
  compilePath,
} from '../../../common/utils/helpers/routes';
import Search from '../search/Search';
import EditResultPage from '../../../pages/edit-result/components/edit-result-page/EditResultPage';
import {
  selectFocused,
  selectSuggestedFeatures,
} from '../../../common/store/search';
import { useMobile } from '../../../common/components/responsive/Responsive';
import { selectActiveItem } from '../../../pages/item-details/store';
import { isPOIItem } from '../../../common/utils/helpers/item';
import RootPage from '../../../pages/root/components/root-page/RootPage';
import AddPlacePage from '../../../pages/item-add/components/add-place-page/AddPlacePage';
import ItemEditPage from '../../../pages/item-edit/components/item-edit-page/ItemEditPage';
import CloseOrRemovePage from '../../../pages/close-or-remove-item/components/close-or-remove-page/CloseOrRemovePage';
import ErrorPage from '../../../pages/error/components/error-page/ErrorPage';
import ItemDetailsPage from '../../../pages/item-details/components/item-details-page/ItemDetailsPage';
import ResultsListPage from '../../../pages/search-results/components/results-list-page/ResultsListPage';

import './Wizard.scss';

const isRoot = pathnameMatches.bind(null, namedRoute('root'), { strict: true });

export interface WizardProps {
  className?: string;
}

export interface RouteMatch {
  wizardStep;
  featureType;
}

const Wizard = ({ className }: WizardProps) => {
  const location = useLocation();

  const searchFocused = useSelector(selectFocused);
  const searchResults = useSelector(selectSuggestedFeatures);

  const isMobile = useMobile();
  const item = useSelector(selectActiveItem);
  const isBusinessEdit = useMemo(() => isPOIItem(item), [item]);

  const editMatch = getPathnameMatch<RouteMatch>(
    namedRoute('edit'),
    location.pathname,
  );
  const addMatch = getPathnameMatch<RouteMatch>(
    namedRoute('add'),
    location.pathname,
  );
  const routablePointsMatch = getPathnameMatch<RouteMatch>(
    namedRoute(
      'routable-points',
      compilePath('edit', null, {
        wizardStep: FEEDBACK_WIZARD_STEP.routablePoints,
      }),
    ),
    location.pathname,
  );

  const shouldHideMobileSearchBarRoutablePoints =
    routablePointsMatch &&
    routablePointsMatch.params.wizardStep === FEEDBACK_WIZARD_STEP.location;

  const shouldHideMobileSearchBarEditPage =
    editMatch &&
    editMatch.params.wizardStep ===
      (isBusinessEdit
        ? FEEDBACK_WIZARD_STEP.location
        : FEEDBACK_WIZARD_STEP.feedback);

  const shouldHideMobileSearchBarAddPage =
    addMatch &&
    ((addMatch.params.featureType === ADD_FEATURE_TYPE.address &&
      addMatch.params.wizardStep === FEEDBACK_WIZARD_STEP.feedback) ||
      (addMatch.params.featureType === ADD_FEATURE_TYPE.business &&
        addMatch.params.wizardStep === FEEDBACK_WIZARD_STEP.location));

  const shouldHideSearchBar =
    isMobile &&
    (shouldHideMobileSearchBarRoutablePoints ||
      shouldHideMobileSearchBarEditPage ||
      shouldHideMobileSearchBarAddPage);

  return (
    <div
      className={classnames(
        'wizard',
        {
          wizard_active: !isRoot(location.pathname),
          'wizard_search-focused': searchFocused,
          'wizard_search-results': searchResults,
        },
        className,
      )}
    >
      <Search
        className={classnames('wizard__search-field', {
          'wizard__search-field_hidden': shouldHideSearchBar,
        })}
      />
      <div className="wizard__content">
        <Switch>
          <Route path={namedRoute('root')} exact={true}>
            <RootPage />
          </Route>

          <Route path={namedRoute('results-list')}>
            <ResultsListPage className="wizard__search-results" />
          </Route>

          <Route path={namedRoute('details')}>
            <ItemDetailsPage className="wizard__item-details" />
          </Route>

          <Route path={namedRoute('edit')}>
            <ItemEditPage className="wizard__item-edit" />
          </Route>

          <Route path={namedRoute('thank-you')}>
            <EditResultPage className="wizard__edit-result" />
          </Route>

          <Route path={[namedRoute('add'), namedRoute('add-root')]}>
            <AddPlacePage />
          </Route>

          <Route path={[namedRoute('remove'), namedRoute('remove-root')]}>
            <CloseOrRemovePage />
          </Route>

          <Route path={namedRoute('error')}>
            <ErrorPage />
          </Route>

          <Redirect to="/" />
        </Switch>
      </div>
    </div>
  );
};

export default Wizard;
